import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Logo } from '@/components/layout/Logo';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

const features = [
  {
    emoji: '📊',
    title: 'Budgeting',
    description:
      'Set monthly budgets by category and track progress with visual indicators. Stay on top of your spending limits.',
  },
  {
    emoji: '🎯',
    title: 'Goals Tracking',
    description:
      'Define savings goals with target dates and milestones. Watch your progress grow over time.',
  },
  {
    emoji: '📈',
    title: 'Analytics & Insights',
    description:
      'Visual breakdowns of spending patterns, trends, and category-level analysis with interactive charts.',
  },
  {
    emoji: '🔄',
    title: 'Subscriptions',
    description:
      'Track recurring charges, detect subscription creep, and stay informed about your commitments.',
  },
  {
    emoji: '⚡',
    title: 'Transaction Rules',
    description:
      'Auto-categorize transactions with custom rules. Less manual work, more accuracy.',
  },
  {
    emoji: '🌍',
    title: 'Multi-Currency',
    description:
      'Support for multiple currencies with automatic conversion. Perfect for global lifestyles.',
  },
];

const steps = [
  {
    number: 1,
    title: 'Create Account',
    description:
      'Sign up for free — no credit card required, no hidden fees. Your data stays yours.',
  },
  {
    number: 2,
    title: 'Add Transactions',
    description:
      'Log your income and expenses manually or create rules that auto-categorize for you.',
  },
  {
    number: 3,
    title: 'Get Insights',
    description:
      'See where your money goes with budgets, analytics, and goal tracking — all in one place.',
  },
];

export default function Landing() {
  useDocumentTitle('Home');
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* NAV BAR */}
      <nav
        className="sticky top-0 z-50 w-full border-b bg-background/80 backdrop-blur-sm"
        aria-label="Main navigation"
      >
        <div className="container mx-auto flex h-14 items-center justify-between px-4 sm:px-6 lg:px-8">
          <Logo />
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/login">Sign In</Link>
            </Button>
            <Button variant="default" size="sm" asChild>
              <Link to="/register">Get Started</Link>
            </Button>
          </div>
        </div>
      </nav>

      {/* §A HERO */}
      <section className="py-20 sm:py-28 lg:py-36">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center max-w-4xl">
          <div className="flex justify-center">
            <Logo className="scale-150" />
          </div>
          <h1 className="mt-8 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
            <span className="bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
              Take Control of Your Finances
            </span>
          </h1>
          <p className="mt-4 text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto">
            The personal finance tracker that costs nothing to run. Track
            spending, manage budgets, and reach your goals — powered by
            serverless architecture.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button size="lg" asChild className="min-h-[44px] min-w-[200px]">
              <Link to="/register">Create Free Account</Link>
            </Button>
            <Button
              variant="outline"
              size="lg"
              asChild
              className="min-h-[44px] min-w-[140px]"
            >
              <Link to="/login">Sign In</Link>
            </Button>
          </div>
          <div className="mt-6">
            <Badge variant="secondary">
              ✨ $0/month — Always free for personal use
            </Badge>
          </div>
        </div>
      </section>

      {/* §B FEATURES GRID */}
      <section className="bg-muted/50 py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight text-center">
            Everything You Need
          </h2>
          <p className="mt-2 text-center text-muted-foreground max-w-2xl mx-auto">
            A complete toolkit for personal finance management.
          </p>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature) => (
              <Card key={feature.title} className="group">
                <CardHeader>
                  <div
                    className="text-3xl group-hover:-translate-y-0.5 transition-transform duration-200"
                    aria-hidden="true"
                  >
                    {feature.emoji}
                  </div>
                  <CardTitle className="mt-2">{feature.title}</CardTitle>
                  <CardDescription>{feature.description}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* §C HOW IT WORKS */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight text-center">
            How It Works
          </h2>
          <p className="mt-2 text-center text-muted-foreground max-w-2xl mx-auto">
            Get started in three simple steps.
          </p>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-10">
            {steps.map((step) => (
              <div key={step.number} className="text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground text-lg font-bold">
                  {step.number}
                </div>
                <h3 className="mt-4 text-xl font-semibold">{step.title}</h3>
                <p className="mt-2 text-muted-foreground">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* §D CTA BANNER */}
      <section className="bg-muted/50 py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center max-w-2xl">
          <h2 className="text-3xl font-bold tracking-tight">
            Ready to Take Control?
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Join FinTrack and start managing your finances today. Free forever.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button size="lg" asChild className="min-h-[44px] min-w-[200px]">
              <Link to="/register">Create Free Account</Link>
            </Button>
            <Button
              variant="outline"
              size="lg"
              asChild
              className="min-h-[44px] min-w-[140px]"
            >
              <Link to="/login">Sign In</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* §E FOOTER */}
      <footer className="py-8">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
          <Badge variant="outline">
            ⚡ Powered by serverless architecture — $0/month hosting
          </Badge>
          <Separator />
          <p className="text-sm text-muted-foreground">
            © 2026 FinTrack. Built with React, Hono & GCP Cloud Run.
          </p>
        </div>
      </footer>
    </div>
  );
}
